/**
 * The moment between picking a mode and arriving in it.
 *
 * Both destinations cost real time — the setup form has shops to fetch,
 * ThreeBuilder is a lazy chunk with three.js inside it — so this is a loading
 * state, not decoration. That is also why it is **driven by rAF rather than by
 * CSS animation**: a CSS `@keyframes` can be suppressed by the OS's
 * reduce-motion setting or by a browser that throttles animations, and a
 * progress indicator that silently stops looks exactly like an app that has
 * hung. A `requestAnimationFrame` loop writing inline styles cannot be turned
 * off by a preference; if frames are being painted at all, this moves.
 *
 * Each mode gets its own sequence, because the two are not the same promise:
 *
 *   scan  — a laser sweeps the frame under a reticle, the way a camera reads a
 *           room. It runs three passes, so it reads as "acquiring", not "a line
 *           moved once".
 *   build — a blueprint grid snaps into place and a room extrudes out of it,
 *           drawn edge by edge. Construction, not capture.
 */

import { useEffect, useRef, useState } from 'react'
import './ModeLaunch.css'

export type LaunchMode = 'scan' | 'threebuilder'

/** How long the sequence runs before the caller navigates. */
export const LAUNCH_MS = 1150

interface Props {
  mode: LaunchMode
  label: string
  /** Called once the sequence has finished. */
  onDone: () => void
}

/** Ease-out so the sweep decelerates into place instead of stopping dead. */
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)
const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

export default function ModeLaunch({ mode, label, onDone }: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const doneRef = useRef(onDone)
  doneRef.current = onDone
  const [pct, setPct] = useState(0)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const start = performance.now()
    let raf = 0
    let finished = false

    const finish = () => {
      if (finished) return
      finished = true
      doneRef.current()
    }

    const tick = (now: number) => {
      const t = clamp01((now - start) / LAUNCH_MS)
      const e = easeOut(t)

      // Everything the stylesheet needs, as numbers it cannot refuse to apply.
      el.style.setProperty('--t', String(t))
      el.style.setProperty('--e', String(e))
      // Three passes for scan, one continuous build for the other.
      el.style.setProperty('--sweep', String(mode === 'scan' ? (t * 3) % 1 : e))
      setPct(Math.round(t * 100))

      if (t < 1) {
        raf = requestAnimationFrame(tick)
        return
      }
      finish()
    }
    raf = requestAnimationFrame(tick)

    // A backstop, because rAF does not run in a background tab. Without it,
    // switching away mid-transition would leave the app parked on this screen
    // until the tab was looked at again.
    const timer = window.setTimeout(finish, LAUNCH_MS + 400)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(timer)
    }
  }, [mode])

  return (
    <div
      ref={rootRef}
      className={`ml-root ml-${mode}`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="ml-stage">
        {mode === 'scan' ? <ScanStage /> : <BuildStage />}
      </div>

      <div className="ml-caption">
        <span className="ml-label">{label}</span>
        <span className="ml-bar">
          <span className="ml-bar-fill" />
        </span>
        <span className="ml-pct">{pct}%</span>
      </div>
    </div>
  )
}

/** Corner reticle plus a sweeping read line — a camera acquiring a room. */
function ScanStage() {
  return (
    <div className="ml-scan">
      <span className="ml-grid" aria-hidden="true" />
      <span className="ml-corner ml-tl" aria-hidden="true" />
      <span className="ml-corner ml-tr" aria-hidden="true" />
      <span className="ml-corner ml-bl" aria-hidden="true" />
      <span className="ml-corner ml-br" aria-hidden="true" />
      <span className="ml-beam" aria-hidden="true" />
      <svg className="ml-shape" viewBox="0 0 120 120" fill="none" aria-hidden="true">
        {/* A shelf bay, seen head-on: what the camera is being pointed at. */}
        <rect x="24" y="26" width="72" height="68" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M24 48h72M24 70h72" stroke="currentColor" strokeWidth="2" />
        <rect x="32" y="33" width="12" height="13" fill="currentColor" opacity="0.55" />
        <rect x="48" y="35" width="10" height="11" fill="currentColor" opacity="0.35" />
        <rect x="34" y="55" width="11" height="13" fill="currentColor" opacity="0.4" />
        <rect x="62" y="53" width="14" height="15" fill="currentColor" opacity="0.5" />
        <rect x="40" y="77" width="13" height="15" fill="currentColor" opacity="0.3" />
      </svg>
    </div>
  )
}

/** A blueprint grid that snaps in while a room extrudes out of it. */
function BuildStage() {
  return (
    <div className="ml-build">
      <span className="ml-blueprint" aria-hidden="true" />
      <svg className="ml-shape" viewBox="0 0 120 120" fill="none" aria-hidden="true">
        {/* Floor plate first, then the walls rise, then the shelving lands.
            Each path's dash offset is driven from --e, so they draw in order. */}
        <path className="ml-draw ml-d0" d="M20 78 60 96 100 78 60 60Z" stroke="currentColor" strokeWidth="2" />
        <path className="ml-draw ml-d1" d="M20 78V52M100 78V52M60 96V70" stroke="currentColor" strokeWidth="2" />
        <path className="ml-draw ml-d2" d="M20 52 60 34l40 18-40 18Z" stroke="currentColor" strokeWidth="2" />
        <path className="ml-draw ml-d3" d="M44 66v-9l16-7 16 7v9l-16 7Z" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
  )
}
